import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Spinner } from "@nextui-org/react";
import { Loader } from "lucide-react";

import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { QuestionResponseField } from "@/shared/components/QuestionResponseField";
import { ROUTES } from "@/shared/constants/routes";
import { changeBusinessOwnerFormState } from "@/modules/third-party-risk/requests/intake-form-v2";
import { IntakeFormQuestionItem } from "@/modules/agent/components/VendorAssessmentIntakeForm/IntakeFormQuestionItem";
import { intakeFormValidationSchema } from "@/modules/agent/components/VendorAssessmentIntakeForm/intake-form.schema";
import type { IntakeFormQuestion } from "@/modules/agent/requests/vendor-intake-form-response";

import { useBusinessOwnerFormQuery } from "./hooks/useBusinessOwnerFormQuery";
import { useAcceptFlow } from "./hooks/useAcceptFlow";
import { useRejectFlow } from "./hooks/useRejectFlow";
import { useAssessmentIntakeFormQuery } from "./hooks/useAssessmentIntakeFormQuery";
import { useIntakeAutoSave } from "./hooks/useIntakeAutoSave";
import { ReviewBanner } from "./components/ReviewBanner";
import { AcceptFlowModals } from "./components/AcceptFlowModals";
import { RejectReasonModal } from "./components/RejectReasonModal";
import { IntakeReviewStepper } from "./components/IntakeReviewStepper";

export default function IntakeReviewPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useBusinessOwnerFormQuery(formId);
  const pickUpCalled = useRef(false);

  // Transition to "in_review" on mount
  useEffect(() => {
    if (formId && data?.businessOwnerForm && !pickUpCalled.current) {
      if (data.businessOwnerForm.intake_form_state === "submitted") {
        pickUpCalled.current = true;
        changeBusinessOwnerFormState(formId, { action: "pick_up" }).catch(
          () => {
            // Intentionally silent — the form may already be in "in_review" state
          },
        );
      }
    }
  }, [formId, data?.businessOwnerForm]);

  const acceptFlow = useAcceptFlow({
    formId: formId ?? "",
    vendorName: data?.businessOwnerForm?.vendor_name ?? "",
    productName: data?.businessOwnerForm?.product_name ?? "",
  });

  const rejectFlow = useRejectFlow({ formId: formId ?? "" });

  // Derive edit mode
  const assessmentId =
    acceptFlow.acceptedAssessmentId ??
    data?.businessOwnerForm?.assessment_id ??
    null;

  const isEditMode =
    !!assessmentId &&
    (!!acceptFlow.acceptedAssessmentId ||
      data?.businessOwnerForm?.intake_form_state === "approved");

  // --- Review mode form (read-only) ---
  const reviewFormMethods = useForm({
    defaultValues: { questions: [] as any[] },
  });

  useEffect(() => {
    if (data?.questions && !isEditMode) {
      reviewFormMethods.reset({ questions: data.questions });
    }
  }, [data?.questions, reviewFormMethods, isEditMode]);

  // --- Edit mode form ---
  const assessmentIntakeQuery = useAssessmentIntakeFormQuery(
    isEditMode ? assessmentId : null,
  );

  const editFormMethods = useForm<{ questions: IntakeFormQuestion[] }>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(intakeFormValidationSchema) as any,
    defaultValues: { questions: [] },
  });

  const { fields } = useFieldArray({
    control: editFormMethods.control,
    name: "questions",
  });

  // Reset edit form when assessment questions load
  useEffect(() => {
    if (assessmentIntakeQuery.data?.questions) {
      const questions = assessmentIntakeQuery.data.questions;
      editFormMethods.reset({
        questions: questions.map((q) => ({
          ...q,
          response:
            q.response !== null && q.response !== undefined
              ? q.response
              : q.question_type === "multiple_choice" ||
                  q.question_type === "file_upload"
                ? []
                : "",
        })),
      });
    }
  }, [assessmentIntakeQuery.data?.questions, editFormMethods]);

  // Auto-save for edit mode
  const autoSave = useIntakeAutoSave({
    assessmentId: assessmentId ?? "",
    setValue: editFormMethods.setValue,
  });

  // Track previous file responses for deletion detection
  const previousResponsesRef = useRef<Map<number, string[]>>(new Map());

  // Watch for changes in edit mode and trigger auto-save
  useEffect(() => {
    if (!isEditMode) return;

    const subscription = editFormMethods.watch((value, { name }) => {
      if (
        name &&
        name.startsWith("questions.") &&
        name.endsWith(".response")
      ) {
        const match = name.match(/questions\.(\d+)\.response/);
        if (match) {
          const index = Number.parseInt(match[1], 10);
          const question = value.questions?.[index];
          if (question) {
            if (
              question.question_type === "file_upload" &&
              Array.isArray(question.response)
            ) {
              const previousResponse =
                previousResponsesRef.current.get(index) || [];
              const currentResponse = question.response.filter(
                (url): url is string => typeof url === "string",
              );
              previousResponsesRef.current.set(index, currentResponse);
              const isDeletion =
                currentResponse.length === 0 && previousResponse.length > 0;
              if (currentResponse.length === 0 && !isDeletion) {
                return;
              }
            }
            void autoSave.debouncedAutoSave(
              question as IntakeFormQuestion,
              index,
            );
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [isEditMode, editFormMethods, autoSave.debouncedAutoSave]);

  // File upload error tracking for edit mode
  const handleFileUploadError = useCallback(
    (_questionIndex: number, _hasError: boolean) => {
      // Tracked locally if needed for submit validation in future steps
    },
    [],
  );

  const breadcrumbItems = [
    {
      title: "Third-Party Risk",
      href: `/${ROUTES.THIRD_PARTY_VENDORS_V2}`,
    },
    ...(data
      ? [
          { title: data.businessOwnerForm.vendor_name, href: "#" },
          { title: data.businessOwnerForm.product_name, href: "#" },
          { title: isEditMode ? "Intake Form" : "New Request", href: "#" },
        ]
      : [{ title: "Loading...", href: "#" }]),
  ];

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex items-center pb-4 min-h-[44px] max-h-[44px]">
          <ZBreadCrumbs items={breadcrumbItems} />
        </div>
        <div className="w-full flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex items-center pb-4 min-h-[44px] max-h-[44px]">
          <ZBreadCrumbs items={breadcrumbItems} />
        </div>
        <div className="w-full flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 bg-white border border-[#E4E4E7] rounded-lg p-8 max-w-md">
            <span className="text-sm text-[#64748B] text-center">
              {error instanceof Error
                ? error.message
                : "Unable to load the intake form. Please try again."}
            </span>
            <ZButton
              variant="bordered"
              size="sm"
              onPress={() => void refetch()}
            >
              Retry
            </ZButton>
          </div>
        </div>
      </div>
    );
  }

  const { businessOwnerForm, questions } = data;

  // --- Edit mode rendering ---
  if (isEditMode) {
    const tierLabel =
      businessOwnerForm.tier != null ? `Tier ${businessOwnerForm.tier}` : null;

    return (
      <div className="w-full h-full flex flex-col">
        {/* Breadcrumbs */}
        <div className="flex items-center pb-4 min-h-[44px] max-h-[44px]">
          <ZBreadCrumbs items={breadcrumbItems} />
        </div>

        {/* Main content */}
        <div className="w-full h-full bg-[#FAFAFA] bg-opacity-90 flex flex-col items-center overflow-auto">
          {/* Header with stepper */}
          <div
            className="flex flex-col items-center w-full"
            style={{
              padding: "16px",
              gap: "16px",
              backgroundColor: "#FFFFFF",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            {/* Top row: title + auto-save indicator + stepper */}
            <div className="flex items-center justify-between self-stretch">
              <div className="flex items-center gap-3">
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "24px",
                    lineHeight: "28px",
                    color: "#020618",
                  }}
                >
                  {businessOwnerForm.vendor_name}{" "}
                  {businessOwnerForm.product_name}
                </span>
                <div className="flex items-center h-7 gap-1">
                  <Loader
                    className={`w-4 h-4 animate-spin ${autoSave.isSaving ? "" : "invisible"}`}
                  />
                  {autoSave.isSaving && (
                    <p className="text-tiny text-[#A5A5A5] text-[10px]">
                      Auto-saving
                    </p>
                  )}
                </div>
              </div>
              <IntakeReviewStepper currentStep={1} />
            </div>

            {/* Subtitle row */}
            <div className="flex items-center gap-3 self-stretch">
              <div className="flex flex-col gap-1">
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#0F172B",
                  }}
                >
                  Review Intake Form Responses
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#64748B",
                  }}
                >
                  Verify the intake details and complete any remaining fields to
                  proceed to tiering.
                </span>
              </div>
              {tierLabel && (
                <span
                  className="rounded-full px-3 py-1 text-xs font-medium"
                  style={{
                    backgroundColor: "#F0F9FF",
                    color: "#006FEE",
                    border: "1px solid #BAE6FD",
                  }}
                >
                  {tierLabel}
                </span>
              )}
            </div>
          </div>

          {/* Questions area — editable */}
          <div className="px-16 py-4 flex-1 w-full">
            {assessmentIntakeQuery.isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : (
              <div className="flex flex-col gap-6 w-full">
                <div className="bg-[#FEFEFE] w-full rounded-lg p-6 flex flex-col gap-6">
                  <FormProvider {...editFormMethods}>
                    <div className="flex flex-col gap-6">
                      {fields.map((field, index) => (
                        <IntakeFormQuestionItem
                          key={field.id}
                          index={index}
                          onFileUploadError={handleFileUploadError}
                          onFileUploadStatusChange={
                            autoSave.handleFileUploadStatusChange
                          }
                          isViewMode={false}
                        />
                      ))}
                      {/* Continue button below the last question */}
                      <div className="pt-2">
                        <ZButton
                          type="button"
                          isDisabled={autoSave.isSaving}
                          className="px-6"
                          onPress={() => navigate(`/agent/${assessmentId}?reviewStep=2`)}
                        >
                          Continue
                        </ZButton>
                      </div>
                    </div>
                  </FormProvider>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Accept flow modals (still needed for "view existing" flow) */}
        <AcceptFlowModals
          modalStep={acceptFlow.modalStep}
          existingAssessment={acceptFlow.existingAssessment}
          memberHasAccess={acceptFlow.memberHasAccess}
          archiveConfirmed={acceptFlow.archiveConfirmed}
          isLoading={acceptFlow.isLoading}
          onClose={acceptFlow.closeModals}
          onViewAssessment={acceptFlow.handleViewExisting}
          onStartNew={acceptFlow.handleStartNew}
          onBackFromArchive={acceptFlow.handleBackFromArchive}
          onToggleConfirm={acceptFlow.setArchiveConfirmed}
          onArchiveProceed={acceptFlow.handleArchiveProceed}
        />
      </div>
    );
  }

  // --- Review mode rendering (default) ---
  return (
    <div className="w-full h-full flex flex-col">
      {/* Breadcrumbs */}
      <div className="flex items-center pb-4 min-h-[44px] max-h-[44px]">
        <ZBreadCrumbs items={breadcrumbItems} />
      </div>

      {/* Main content */}
      <div className="w-full h-full bg-[#FAFAFA] bg-opacity-90 flex flex-col items-center overflow-auto">
        {/* Section header — white bg with vendor title + review card per Figma */}
        <div
          className="flex flex-col items-center w-full"
          style={{
            padding: "16px",
            gap: "16px",
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          {/* Section title row */}
          <div className="flex items-center self-stretch">
            <span
              style={{
                fontWeight: 600,
                fontSize: "24px",
                lineHeight: "28px",
                color: "#020618",
              }}
            >
              {businessOwnerForm.vendor_name} {businessOwnerForm.product_name}
            </span>
          </div>

          {/* Blue review card */}
          <ReviewBanner
            businessOwnerForm={businessOwnerForm}
            onAccept={acceptFlow.startAcceptFlow}
            onReject={rejectFlow.openRejectModal}
            isLoading={acceptFlow.isLoading}
          />
        </div>

        {/* Questions area */}
        <div className="px-16 py-4 flex-1 w-full">
          <div className="flex flex-col gap-6 w-full">
            {/* White card container for questions */}
            <div className="bg-[#FEFEFE] w-full rounded-lg p-6 flex flex-col gap-6">
              <h3
                className="font-semibold text-[#0F172B]"
                style={{ fontSize: "16px", lineHeight: "24px" }}
              >
                Intake Form Response
              </h3>

              <FormProvider {...reviewFormMethods}>
                <div className="flex flex-col gap-6">
                  {questions.map((_q, index) => (
                    <QuestionResponseField
                      key={_q.id}
                      index={index}
                      isViewMode
                    />
                  ))}
                </div>
              </FormProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Reject flow modal */}
      <RejectReasonModal
        isOpen={rejectFlow.showRejectModal}
        isLoading={rejectFlow.isLoading}
        rejectionReason={rejectFlow.rejectionReason}
        onReasonChange={rejectFlow.setRejectionReason}
        onClose={rejectFlow.closeRejectModal}
        onReject={rejectFlow.handleReject}
      />

      {/* Accept flow modals */}
      <AcceptFlowModals
        modalStep={acceptFlow.modalStep}
        existingAssessment={acceptFlow.existingAssessment}
        memberHasAccess={acceptFlow.memberHasAccess}
        archiveConfirmed={acceptFlow.archiveConfirmed}
        isLoading={acceptFlow.isLoading}
        onClose={acceptFlow.closeModals}
        onViewAssessment={acceptFlow.handleViewExisting}
        onStartNew={acceptFlow.handleStartNew}
        onBackFromArchive={acceptFlow.handleBackFromArchive}
        onToggleConfirm={acceptFlow.setArchiveConfirmed}
        onArchiveProceed={acceptFlow.handleArchiveProceed}
      />
    </div>
  );
}
